import { LitElement, html } from 'lit';
import { t } from '../i18n';
import {
  GMAIL_CATEGORY_IDS,
  mergeDiscoveredIds,
  toggleGmailCategory,
  type GmailCategoryId,
  type GmailFilterDraft,
  type ProviderDiscovery,
} from '../connection-sync-rules-logic';
import { defineZenComponent } from './register';

export class ZenGmailSyncFilterEditor extends LitElement {
  static properties = {
    draft: { attribute: false },
    discovery: { attribute: false },
    disabled: { type: Boolean },
    saving: { type: Boolean },
    globalDirty: { type: Boolean },
    accountDirty: { type: Boolean },
  };

  declare draft: GmailFilterDraft;
  declare discovery: ProviderDiscovery;
  declare disabled: boolean;
  declare saving: boolean;
  declare globalDirty: boolean;
  declare accountDirty: boolean;

  constructor() {
    super();
    this.draft = { include_categories: [], exclude_senders: [], exclude_recipients: [], exclude_labels: [] };
    this.discovery = { status: 'idle', items: [] };
    this.disabled = false;
    this.saving = false;
    this.globalDirty = false;
    this.accountDirty = false;
  }

  createRenderRoot() { return this; }

  private emitDraft(next: GmailFilterDraft) {
    this.dispatchEvent(new CustomEvent('filter-change', { detail: next, bubbles: true, composed: true }));
  }

  private toggleCategory(category: GmailCategoryId, checked: boolean) {
    // An empty include_categories array falls back to the main-inbox default
    // at runtime. Keep at least one visible selection so the form and runtime agree.
    const next = toggleGmailCategory(this.draft.include_categories, category, checked);
    if (next.length === this.draft.include_categories.length
      && next.every((item, index) => item === this.draft.include_categories[index])) return;
    this.emitDraft({ ...this.draft, include_categories: next });
  }

  private toggleLabel(id: string, checked: boolean) {
    const next = checked
      ? [...new Set([...this.draft.exclude_labels, id])]
      : this.draft.exclude_labels.filter((item) => item !== id);
    this.emitDraft({ ...this.draft, exclude_labels: next });
  }

  render() {
    const readOnly = this.disabled || this.saving || this.discovery.status !== 'available';
    const labels = mergeDiscoveredIds(this.discovery.items, this.draft.exclude_labels);
    return html`
      ${this.discovery.status === 'loading' ? html`<p class="sync-filter-note">${t('connectionFilterLoading')}</p>` : ''}
      ${this.discovery.status === 'degraded' ? html`
        <p class="sync-filter-degraded"><span class="mi mi-sm" aria-hidden="true">info</span>${t('connectionFilterDegraded')}</p>
      ` : ''}
      <fieldset class="sync-filter-fieldset" ?disabled=${readOnly}>
        <legend>${t('gmailFilterCategories')}</legend>
        <div class="sync-filter-check-grid">
          ${GMAIL_CATEGORY_IDS.map((category) => html`
            <label><input type="checkbox" .checked=${this.draft.include_categories.includes(category)}
              @change=${(event: Event) => this.toggleCategory(category, (event.target as HTMLInputElement).checked)}>
              ${t(`gmailCategory_${category}`)}</label>
          `)}
        </div>
      </fieldset>
      <div class="sync-filter-address-grid">
        <label>${t('gmailFilterExcludedSenders')}
          <textarea .value=${this.draft.exclude_senders.join('\n')} ?disabled=${readOnly}
            @input=${(event: Event) => this.emitDraft({ ...this.draft, exclude_senders: (event.target as HTMLTextAreaElement).value.split(/[\n,、;]+/) })}></textarea>
        </label>
        <label>${t('gmailFilterExcludedRecipients')}
          <textarea .value=${this.draft.exclude_recipients.join('\n')} ?disabled=${readOnly}
            @input=${(event: Event) => this.emitDraft({ ...this.draft, exclude_recipients: (event.target as HTMLTextAreaElement).value.split(/[\n,、;]+/) })}></textarea>
        </label>
      </div>
      <fieldset class="sync-filter-fieldset" ?disabled=${readOnly}>
        <legend>${t('gmailFilterLabels')}</legend>
        <p class="sync-filter-note">${t('gmailFilterLabelsHint')}</p>
        <div class="sync-filter-list">
          ${labels.map((label) => html`<label>
            <input type="checkbox" .checked=${this.draft.exclude_labels.includes(label.id)}
              @change=${(event: Event) => this.toggleLabel(label.id, (event.target as HTMLInputElement).checked)}>
            <span>${label.name}</span><small>${label.id}</small>
          </label>`)}
        </div>
      </fieldset>
      <div class="sync-filter-save-actions">
        <button type="button" class="btn btn-primary btn-sm" ?disabled=${readOnly || !(this.globalDirty || this.accountDirty)}
          @click=${() => this.dispatchEvent(new CustomEvent('filter-save', { detail: this.draft, bubbles: true, composed: true }))}>
          <span class="mi mi-xs" aria-hidden="true">save</span>${t('connectionFilterSave')}
        </button>
      </div>
    `;
  }
}

defineZenComponent('zen-gmail-sync-filter-editor', ZenGmailSyncFilterEditor);
